import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import {
  AdditiveBlending,
  BackSide,
  Color,
  DoubleSide,
  type Group,
  type Mesh,
  type Sprite,
  type Texture,
} from 'three';
import type { NewsStatus, Planet, Task } from '@map/shared';
import { prefersReducedMotion, sceneColor, sceneHealthColor } from '../styles/tokens.js';
import { glowTexture, planetSkin } from './planetSkin.js';

import { ShootingStars } from './ShootingStars.js';
import { Starfield } from './Starfield.js';
import { FieldCard, TaskField, type FieldHover, type Obstacle } from './TaskField.js';
import { Satellite, satelliteLabel } from './Satellite.js';
// Planets used to sit close enough to read as a stack from the default
// framing. They stand well apart now, and each one is given its own slice of
// the circle to start in (see PHASE_STEP) so two never bunch up as they orbit.
// The belt and camera numbers are a checked pair — see globe/framing.
import {
  CAMERA_FLOOR,
  CAMERA_LIFT,
  CAMERA_REACH,
  FIELD_OF_VIEW,
  ORBIT_INNER,
  ORBIT_STEP,
  fitFor,
} from './framing.js';
import { planetTraits, type MoonTrait, type RingTrait } from './planetTraits.js';

/**
 * MAP-105 / MAP-126. The top tier: every life domain as a planet, sized by its
 * importance and tinted by the state of everything inside it.
 *
 * This is the screen that answers "how is my world doing" before any list does.
 * The planet in the worst state should be obvious without reading a word.
 */
export interface SolarSystemProps {
  planets: Planet[];
  onEnter: (planetId: string) => void;
  onOpenStar?: (() => void) | undefined;
  /** Captured tasks, belonging to no planet: open ones are rock, done ones stars. */
  tasks?: Task[];
  /** Clicking one opens it: the panel is where a task is read and changed. */
  onOpenTask?: ((task: Task) => void) | undefined;
  /** A phone: no hover, less room, and a finger instead of a pointer. */
  mobile?: boolean;
  /** The Newswire satellite: drawn only when there is somewhere to open it. */
  newsStatus?: NewsStatus | undefined;
  onOpenNews?: (() => void) | undefined;
}

/**
 * The angle between one planet's starting position and the next. A whole turn
 * divided by the golden ratio: successive planets land as far from all the
 * previous ones as the circle allows, however many there are, so they never
 * line up and never cluster.
 */
const PHASE_STEP = Math.PI * 2 * 0.618034;

/** Moons are bare rock everywhere. Rings take their colour from their planet. */
const MOON_COLOUR = '#9aa4b8';

function Star({ onOpen }: { onOpen?: (() => void) | undefined }) {
  const colour = useMemo(() => new Color(sceneColor('trace-lights', '#f5d58a')), []);
  const glow = useMemo(() => glowTexture(), []);
  useEffect(() => () => glow?.dispose(), [glow]);
  return (
    <group>
      <mesh
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onOpen?.();
        }}
        onPointerOver={() => onOpen && (document.body.style.cursor = 'pointer')}
        onPointerOut={() => onOpen && (document.body.style.cursor = '')}
      >
        <sphereGeometry args={[0.55, 48, 48]} />
        <meshBasicMaterial color={colour} />
      </mesh>
      {glow && (
        <sprite scale={[2.6, 2.6, 1]}>
          <spriteMaterial
            map={glow}
            color={colour}
            transparent
            blending={AdditiveBlending}
            depthWrite={false}
          />
        </sprite>
      )}
      <pointLight intensity={40} distance={40} decay={2} color={colour} />
    </group>
  );
}

function OrbitRing({ radius }: { radius: number }) {
  const points = useMemo(() => {
    const out = new Float32Array(129 * 3);
    for (let i = 0; i <= 128; i += 1) {
      const angle = (i / 128) * Math.PI * 2;
      out[i * 3] = Math.cos(angle) * radius;
      out[i * 3 + 1] = 0;
      out[i * 3 + 2] = Math.sin(angle) * radius;
    }
    return out;
  }, [radius]);

  return (
    <line>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[points, 3]} />
      </bufferGeometry>
      <lineBasicMaterial color="#5a6b85" transparent opacity={0.18} />
    </line>
  );
}

function Ring({ ring, size, colour }: { ring: RingTrait; size: number; colour: string }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[size * ring.innerScale, size * ring.outerScale, 96]} />
      <meshBasicMaterial
        color={colour}
        transparent
        opacity={ring.opacity}
        side={DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
}

function Moon({ moon, size, still }: { moon: MoonTrait; size: number; still: boolean }) {
  const group = useRef<Group>(null);
  const angle = useRef(moon.phase);

  useFrame((_, delta) => {
    if (!still) angle.current += delta * moon.speed;
    const radius = size * moon.distance;
    group.current?.position.set(Math.cos(angle.current) * radius, 0, Math.sin(angle.current) * radius);
  });

  return (
    <group ref={group}>
      <mesh>
        <icosahedronGeometry args={[size * moon.size, 1]} />
        <meshStandardMaterial flatShading color={MOON_COLOUR} roughness={1} />
      </mesh>
    </group>
  );
}

/**
 * Frames the whole system and lets you push in and out.
 *
 * The camera used to sit at a fixed distance, which was fine for four planets
 * and cropped the fifth: the framing is derived from the outermost orbit now,
 * so a system never outgrows its own view.
 */
function SystemCamera({ outerRadius }: { outerRadius: number }) {
  const { camera, gl, invalidate, size } = useThree();
  // The system opens all the way out: 1 is as far back as the camera goes and
  // frames the whole sky — planets, the rocks orbiting outside them and the
  // stars beyond those. Zooming only ever comes in from there.
  const zoom = useRef(1);
  // Where the zoom is heading. Each notch used to land the moment it arrived,
  // which read as the system jumping about; it eases there now.
  const wanted = useRef(1);

  /**
   * A narrow window sees a narrower slice of the world at the same distance,
   * so it stands back until the whole belt fits across it.
   *
   * This used to switch on at exactly 1.0, which meant a window one pixel
   * wider than it was tall framed the system quite differently from one a
   * pixel narrower, and anything squarer than about 4:3 lost the sides of the
   * belt off the edges. The pull-back is continuous now and starts as soon as
   * the horizontal field becomes the binding one — 1.37 is where the belt's
   * width begins to exceed what the camera can hold, worked out from the
   * field of view rather than guessed.
   */
  const aspect = size.width / Math.max(1, size.height);
  const fit = fitFor(aspect);

  const place = useCallback(() => {
    const distance = Math.max(CAMERA_FLOOR, outerRadius * 1.55) * fit * zoom.current;
    camera.position.set(0, distance * CAMERA_LIFT, distance);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    invalidate();
  }, [camera, invalidate, outerRadius, fit]);

  useEffect(place, [place]);

  // Ease toward whatever the last gesture asked for, in step with the clock
  // rather than with the frame rate.
  useFrame((_, delta) => {
    const gap = wanted.current - zoom.current;
    if (Math.abs(gap) < 1e-4) return;
    zoom.current += gap * (1 - Math.exp(-delta * 9));
    place();
  });

  useEffect(() => {
    const canvas = gl.domElement;
    const apply = (factor: number) => {
      wanted.current = Math.min(1, Math.max(0.22, wanted.current * factor));
      invalidate();
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      apply(Math.exp(Math.min(240, Math.max(-240, pixels)) * 0.0016));
    };

    // Pinch, for touch.
    let pinch: number | null = null;
    const spread = (touches: TouchList) => {
      const [a, b] = [touches[0], touches[1]];
      if (!a || !b) return null;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      const distance = spread(event.touches);
      if (distance === null) return;
      if (pinch !== null && distance > 0) apply(pinch / distance);
      pinch = distance;
    };
    const onTouchEnd = () => {
      pinch = null;
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', onTouchEnd);
    return () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, [gl, place, invalidate]);

  return null;
}

/**
 * The one signal the system view gives about what is inside a planet: its
 * aura. Whatever a place looks like on the ground (fire, a poisoned river,
 * camels running off), each thing reports only how neglected it is, and the
 * planet's air shows the worst of it: clear, amber while something is slipping,
 * red and slowly pulsing once something is acute. It follows the worst problem
 * rather than the count, so five things wrong is no louder than one bad one,
 * and the pulse means red never relies on colour alone. The names are in the
 * legend's hover card, not in the sky.
 */
const SHOWS = 0.12;
const ACUTE = 0.8;

type AuraLevel = 'fine' | 'slipping' | 'acute';

function auraLevel(neglect: Planet['neglect']): AuraLevel {
  if (neglect.worst >= ACUTE) return 'acute';
  if (neglect.worst >= SHOWS) return 'slipping';
  return 'fine';
}

function PlanetAura({
  neglect,
  size,
  lift,
  glow,
  still,
}: {
  neglect: Planet['neglect'];
  size: number;
  /** The planet grows under the pointer; its air has to grow with it. */
  lift: number;
  glow: Texture | null;
  still: boolean;
}) {
  const { camera } = useThree();
  const halo = useRef<Sprite>(null);
  const rim = useRef<Mesh>(null);
  const level = auraLevel(neglect);
  const colour = useMemo(
    () =>
      new Color(
        level === 'fine'
          ? sceneColor('atmosphere', '#86b6e0')
          : sceneHealthColor(level === 'acute' ? 1 : 0.55),
      ),
    [level],
  );
  // How strong within its level: the worst problem, and a little for breadth.
  const strength = Math.min(1, neglect.worst + (neglect.troubles.length - 1) * 0.03);

  useFrame((state) => {
    const time = still ? 0 : state.clock.elapsedTime;
    const pulse =
      level === 'acute' ? 0.7 + Math.sin(time * 2.2) * 0.3 : level === 'slipping' ? 0.9 + Math.sin(time * 0.8) * 0.1 : 1;
    if (rim.current) {
      const base = level === 'fine' ? 0.1 : level === 'slipping' ? 0.3 + strength * 0.3 : 0.75;
      (rim.current.material as { opacity: number }).opacity = base * pulse;
    }
    if (halo.current) {
      // Sized against the camera as well as the planet, so pulling back never
      // shrinks it to nothing.
      const unit = Math.max(size * 0.5, camera.position.length() * 0.012);
      const across = (size * 2 + unit * 2.4) * (level === 'acute' ? 1.3 : 1) * (0.92 + pulse * 0.12);
      halo.current.scale.set(across, across, 1);
      (halo.current.material as { opacity: number }).opacity =
        (level === 'acute' ? 0.9 : 0.2 + strength * 0.35) * pulse;
    }
  });

  return (
    <group>
      {/* Additive and back-faced, so it gathers at the edge the way air does. */}
      <mesh ref={rim} scale={1.06 * lift}>
        <sphereGeometry args={[size, 32, 32]} />
        <meshBasicMaterial
          color={colour}
          transparent
          opacity={0.1}
          blending={AdditiveBlending}
          depthWrite={false}
          side={BackSide}
        />
      </mesh>
      {glow && level !== 'fine' && (
        <sprite ref={halo} renderOrder={1}>
          <spriteMaterial
            map={glow}
            color={colour}
            transparent
            opacity={0}
            depthWrite={false}
            blending={AdditiveBlending}
          />
        </sprite>
      )}
    </group>
  );
}

/** One line per problem, in the words of the thing it is about. */
function describeTrouble(trouble: Planet['neglect']['troubles'][number]): string {
  if (trouble.kind === 'salah') return `prayer at ${(trouble.rating ?? 0).toFixed(1)} / 5`;
  if (trouble.kind === 'faculty') return `${Math.round((1 - trouble.weathering) * 100)}% of the quiz held`;
  const verb = trouble.unit === 'ayah' ? 'read' : 'touched';
  if (trouble.quietDays === null) return `never ${verb}`;
  return `${verb} ${trouble.quietDays} ${trouble.quietDays === 1 ? 'day' : 'days'} ago`;
}

function OrbitingPlanet({
  planet,
  radius,
  index,
  still,
  onEnter,
  onHover,
  hovered,
  glow,
  spots,
}: {
  planet: Planet;
  radius: number;
  index: number;
  still: boolean;
  onEnter: (id: string) => void;
  onHover: (id: string | null) => void;
  hovered: boolean;
  glow: Texture | null;
  /** Where this planet is, reported each frame so rocks can keep off it. */
  spots: MutableRefObject<Map<string, Obstacle>>;
}) {
  const group = useRef<Group>(null);
  const body = useRef<Mesh>(null);
  const angle = useRef(index * PHASE_STEP);

  // Importance sets the size, exactly as it does on the surface.
  const size = 0.16 + Math.min(planet.importanceWeight, 8) * 0.07;
  const colour = useMemo(() => new Color(sceneHealthColor(planet.health)), [planet.health]);
  // Its face: fixed for the life of the planet, and the same on every device.
  const traits = useMemo(() => planetTraits(planet.colourSeed), [planet.colourSeed]);
  const skin = useMemo(() => planetSkin(traits, planet.colourSeed), [traits, planet.colourSeed]);
  // A canvas texture holds a GPU allocation, so it goes when the planet does.
  useEffect(() => () => skin?.dispose(), [skin]);
  // How far the planet reaches: its body, or its rings and moons if wider.
  const reach = useMemo(
    () =>
      size *
      Math.max(
        1.25,
        traits.ring?.outerScale ?? 0,
        ...traits.moons.map((moon) => moon.distance + moon.size),
      ),
    [size, traits],
  );
  useEffect(() => {
    const map = spots.current;
    return () => {
      map.delete(planet.id);
    };
  }, [spots, planet.id]);

  useFrame((_, delta) => {
    if (!still) {
      // Outer planets move more slowly, which reads as a system rather than a
      // carousel.
      angle.current += (delta * 0.06) / Math.sqrt(radius);
      if (body.current) body.current.rotation.y += delta * traits.spin;
    }
    if (group.current) {
      group.current.position.set(
        Math.cos(angle.current) * radius,
        0,
        Math.sin(angle.current) * radius,
      );
    }
    spots.current.set(planet.id, {
      x: Math.cos(angle.current) * radius,
      y: 0,
      z: Math.sin(angle.current) * radius,
      r: reach * (hovered ? 1.12 : 1),
    });
  });

  const lift = hovered ? 1.12 : 1;

  return (
    <group ref={group}>
      {/* Tilt carries the body, its rings and its moons together, the way an
          axis does. Tilting the planet alone would read as a wobble. */}
      <group rotation={[0, 0, traits.tilt]}>
        <mesh
          ref={body}
          scale={[lift, lift * traits.flattening, lift]}
          onPointerOver={(e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            onHover(planet.id);
          }}
          onPointerOut={() => onHover(null)}
          onClick={(e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();
            onEnter(planet.id);
          }}
        >
          {traits.surface === 'rocky' ? (
            <icosahedronGeometry args={[size, 2]} />
          ) : (
            <sphereGeometry args={[size, 48, 32]} />
          )}
          {/* The body wears only its own colour. Health is light cast on it,
              never paint: a feverish world, not a repainted one. */}
          <meshStandardMaterial
            flatShading={traits.surface === 'rocky'}
            map={skin}
            color={skin ? '#ffffff' : colour}
            roughness={0.85}
            emissive={colour}
            emissiveIntensity={hovered ? 0.12 : 0.04}
          />
        </mesh>

        {traits.ring && <Ring ring={traits.ring} size={size} colour={traits.palette.pole} />}
        {traits.moons.map((moon, i) => (
          <Moon key={i} moon={moon} size={size} still={still} />
        ))}
      </group>

      <PlanetAura neglect={planet.neglect} size={size} lift={lift} glow={glow} still={still} />
    </group>
  );
}

export function SolarSystem({
  planets,
  onEnter,
  onOpenStar,
  tasks = [],
  onOpenTask,
  mobile = false,
  newsStatus,
  onOpenNews,
}: SolarSystemProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [satelliteHovered, setSatelliteHovered] = useState(false);
  const [hoveredTask, setHoveredTask] = useState<FieldHover | null>(null);
  const still = prefersReducedMotion();
  const glow = useMemo(() => glowTexture(), []);
  useEffect(() => () => glow?.dispose(), [glow]);
  // Each planet's place this frame: the rocks lay themselves out around them.
  const spots = useRef(new Map<string, Obstacle>());

  const ordered = useMemo(() => [...planets].sort((a, b) => a.orbitIndex - b.orbitIndex), [planets]);
  // Only what is still outstanding. Finished work sails its planet's ocean and
  // dropped work was let go, so neither belongs in the sky.
  const floating = useMemo(() => tasks.filter((t) => t.status === 'open'), [tasks]);
  const span = ORBIT_INNER + Math.max(0, ordered.length - 1) * ORBIT_STEP;

  return (
    <div className="relative h-full w-full">
      <Canvas
        camera={{ position: [0, 5, 9.5], fov: FIELD_OF_VIEW }}
        dpr={[1, 2]}
        gl={{ antialias: true }}
        onPointerMissed={() => setHoveredTask(null)}
        style={{ cursor: hovered || hoveredTask || satelliteHovered ? 'pointer' : 'default' }}
      >
        {/* The canvas used to be transparent, so each halo composited against
            the page gradient behind it and read as a dark disc. The scene owns
            its sky now. */}
        <color attach="background" args={[sceneColor('space-deep', '#05070d')]} />
        <ambientLight intensity={0.42} />
        {/* The sky is scenery again. Finished work does not live up here any
            more — it sails its planet's ocean — so a star can go back to
            meaning nothing in particular. */}
        <Starfield />
        <ShootingStars />
        {/* Just past the rock belt, which sits just past the last orbit. The
            belt's radius and this number are a pair: the belt is placed so
            that all of it is inside the frame this produces, so widening one
            without the other puts rocks off the edge of the window. */}
        <SystemCamera outerRadius={span * CAMERA_REACH} />
        <Star onOpen={onOpenStar} />
        {onOpenNews && (
          <Suspense fallback={null}>
          <Satellite
            status={newsStatus}
            onOpen={() => {
              setSatelliteHovered(false);
              onOpenNews();
            }}
            still={still}
            mobile={mobile}
            onHover={setSatelliteHovered}
            spots={spots}
          />
          </Suspense>
        )}
        <TaskField
          tasks={floating}
          span={span}
          still={still}
          hoveredId={hoveredTask?.task.id ?? null}
          onHover={setHoveredTask}
          onOpen={onOpenTask}
          compact={mobile}
          obstacles={spots}
        />
        {ordered.map((planet, index) => {
          const radius = ORBIT_INNER + index * ORBIT_STEP;
          return (
            <group key={planet.id}>
              <OrbitRing radius={radius} />
              <OrbitingPlanet
                planet={planet}
                radius={radius}
                index={index}
                still={still}
                onEnter={onEnter}
                onHover={setHovered}
                hovered={hovered === planet.id}
                glow={glow}
                spots={spots}
              />
            </group>
          );
        })}
      </Canvas>

      <TroubleCard planet={ordered.find((p) => p.id === hovered)} />
      {satelliteHovered && !mobile && (
        <div
          className="pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 rounded-[var(--radius-md)] px-3 py-1.5 text-sm"
          style={{ background: 'color-mix(in oklch, var(--surface-1) 90%, transparent)', color: 'var(--sat-beacon)' }}
        >
          {satelliteLabel(newsStatus)}
        </div>
      )}
      <FieldCard hover={hoveredTask} mobile={mobile} />

      <div
        className={
          mobile
            ? 'pointer-events-auto absolute inset-x-0 bottom-0 flex snap-x snap-mandatory gap-2 overflow-x-auto px-3 pb-3 pt-6'
            : 'pointer-events-none absolute inset-x-0 bottom-6 flex flex-wrap justify-center gap-2 px-4 py-2'
        }
        style={{
          background: 'linear-gradient(to top, var(--space-deep), transparent)',
          ...(mobile
            ? {
                paddingBottom: 'calc(var(--safe-bottom) + 0.75rem)',
                scrollbarWidth: 'none' as const,
              }
            : {}),
        }}
      >
        {ordered.map((planet) => (
          <button
            key={planet.id}
            onClick={() => onEnter(planet.id)}
            {...(mobile ? {} : { onMouseEnter: () => setHovered(planet.id), onMouseLeave: () => setHovered(null) })}
            className={
              mobile
                ? 'tap pointer-events-auto flex shrink-0 snap-start items-center gap-2 rounded-[var(--radius-md)] px-4 py-2.5 text-sm'
                : 'pointer-events-auto flex items-center gap-2 rounded-[var(--radius-md)] px-3 py-1.5 text-sm'
            }
            style={{
              background:
                hovered === planet.id ? 'var(--surface-2)' : 'color-mix(in oklch, var(--surface-1) 70%, transparent)',
            }}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{
                // The dot is the planet's own colour, so the legend and the sky
                // agree; the ring around it is the health the planet is in.
                background: planetTraits(planet.colourSeed).palette.equator,
                boxShadow: `0 0 0 2px ${sceneHealthColor(planet.health)}`,
              }}
              aria-hidden
            />
            {planet.name}
            {auraLevel(planet.neglect) !== 'fine' && (
              <span
                className="text-xs font-semibold"
                style={{ color: auraLevel(planet.neglect) === 'acute' ? 'var(--health-4)' : 'var(--health-2)' }}
              >
                {auraLevel(planet.neglect) === 'acute' ? '▲' : '●'} {planet.neglect.troubles.length}{' '}
                {auraLevel(planet.neglect) === 'acute' ? 'need you' : 'slipping'}
              </span>
            )}
            {planet.overdueTasks > 0 && (
              <span className="text-xs text-text-muted">{planet.overdueTasks} late</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** What is wrong on the planet under the pointer, worst first. */
function TroubleCard({ planet }: { planet: Planet | undefined }) {
  if (!planet || planet.neglect.troubles.length === 0) return null;
  return (
    <div
      className="pointer-events-none absolute bottom-24 left-1/2 w-72 max-w-[calc(100vw-1.5rem)] -translate-x-1/2 rounded-[var(--radius-md)] px-3 py-2 text-sm"
      style={{ background: 'color-mix(in oklch, var(--surface-1) 90%, transparent)' }}
    >
      <div className="mb-1 font-semibold">{planet.name}</div>
      {planet.neglect.troubles.map((trouble) => (
        <div key={trouble.name} className="flex items-baseline gap-2">
          <span
            aria-hidden
            style={{ color: trouble.weathering >= ACUTE ? 'var(--health-4)' : 'var(--health-2)' }}
          >
            {trouble.weathering >= ACUTE ? '▲' : '●'}
          </span>
          <span className="truncate">{trouble.name}</span>
          <span className="ml-auto shrink-0 text-xs text-text-muted">{describeTrouble(trouble)}</span>
        </div>
      ))}
    </div>
  );
}
